/*    
<summary>
   This class component is all about Managing Product data functionality.
   Developer:Aashish Singh, Created Date:29-August-2023
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/
import { action, computed, makeObservable, observable } from "mobx";
import moment from "moment";
import toast from "react-hot-toast";
import { emptyValue } from "../../constants/constants";
import URLConstants from "../../constants/url-constants";
import { getBusinessTypeIdFromLocalStorage, getLanguageFromLocalStorage, getStoreIdFromLocalStorage, getUserTypeFromLocalStorage } from "../../helpers/localstorage-helper";
import { IObservableInitialState, IOption } from "../../models/ICommon";
import IApiResponse, {
  IApiSuccessResponse,
} from "../../models/response/IApiResponse";
import { ICommonState } from "../../models/state/ICommonState";
import { formatMessage } from "../../translations/format-message";
import { initialState } from "../initialState/add-edit-product-state";
import { initialState as GetAllProductManagementState } from "../initialState/get-all-product-state";
import * as baseService from "../service/base-service";
import { AuthStore } from "./auth-store";

import {
  
  registrationTypeOptions,
} from "../../constants/options.constant";
import config from "../../helpers/config-helper";
import { IAddProductManagement } from "../../models/forms/IAddProduct";
import {
  IProductFilter,
  IProductInitialFilterState,
  IProductStoreFilter,
} from "../../models/IFilter";
import {
  IProductManagementList,
  IProductManagementListVM,
  IProductManagementVM,
} from "../../models/response/IProductManagementResponse";
import { productInitialFilterState, productInitialStoreFilterState } from "../initialState/initial-filter-state";
import userTypeEnum from "../../constants/enums/user-type-enum";

const appConfig = config();

const dateFormat = appConfig.REACT_APP_DATE_FORMAT;
const authStore = new AuthStore();

export class ProductManagementStore implements ICommonState {
  inProgress = false;
  error = "";
  productList: IProductManagementListVM = GetAllProductManagementState;

  initialStateValue: IObservableInitialState = {
    success: false,
    error: "",
    inProgress: false,
  };

  addUpdateProductManagementState = { ...this.initialStateValue };
  deleteProductManagementState = { ...this.initialStateValue };
  addUpdateStoreProductOrderState = { ...this.initialStateValue };

  activeInactiveState = { ...this.initialStateValue };

  product: IProductManagementVM | undefined = undefined;
  productState = { ...this.initialStateValue };
  productFilter: IProductFilter = { ...productInitialFilterState };
  getProductDataAccordionState = { ...this.initialStateValue };
  productAccordionData: Map<number, any[]> = new Map();;
  productStoreFilter: IProductStoreFilter = { ...productInitialStoreFilterState };

  constructor() {
    makeObservable(this, {
      inProgress: observable,
      error: observable,
      productList: observable,
      product: observable,
      addUpdateProductManagementState: observable,
      deleteProductManagementState: observable,
      GetProductListByCatIdService: observable,
      productState: observable,
      getProductDataAccordionState: observable,
      productAccordionData: observable,
      productStoreFilter: observable,
      addUpdateStoreProductOrderState: observable,
      productFilter: observable,
      GetProductManagementListService: action,
      GetProductListBySearchService: action,
      setProductStoreFilterDetail: action,
      resetproductStoreFilterDetail: action,
      clearProductData: action,
      GetSearchProductRegistrationFilterService: action,
      AddProductManagementService: action,
      resetAddUpdateStoreProductOrderState: action,
      UpdateProductManagementService: action,
      UpdateProductManagementStatusService: action,
      DeleteProductManagementService: action,
      GetProductManagementByIdService: action,
      UpdateStoreProductOrderIdService: action,
      reset: action,
      resetProduct: action,
      resetProductManagementDetail: action,
      resetAddUpdateProductManagementState: action,
      setProductFilterDetail: action,
      allProductManagement: computed,
      productDetail: computed,
      getProductFilter: computed,
      getProductData: computed,
      getProductStoreFilter: computed,
    });
  }

  /**
   * Fetches the list of product registrations based on the provided filters and pagination details.
   * @param currentPage - Current Page Number (used for pagination)
   * @param pagerSize - Number of items per page (used for pagination)
   * @param orderBy - Sorting order ("asc" or "desc")
   * @param column - Column to sort by (e.g., "productName")
   * @param filter - Filter object containing productName, businessType, category, and is_invalid
   */

  GetProductManagementListService = (
    currentPage: number,
    pagerSize: number,
    orderBy: string,
    column: string,
    filter: IProductInitialFilterState,
    allChildIds: number[]
  ) => {
    this.inProgress = true;
    const categoryQuery =
      filter.CategoryId !== -1 && filter.BusinessTypeId !== -1
        ? [filter.CategoryId, ...allChildIds]
            .map(id => `&category=${id}`)
            .join("")
        : "";
    const url = `${URLConstants.GetProductManagementFilter
      }?PageNo=${currentPage}&PageSize=${pagerSize}&productName=${filter.ProductName
      }&OrderBy=${orderBy}&Column=${column}&is_invalid=${filter.IsInvalid}${filter.BusinessTypeId !== -1
        ? `&businessType=${filter.BusinessTypeId}`
        : ""
      }${categoryQuery}`;

    return baseService
      .getRequest(url)
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<IProductManagementListVM>>
        ) => {
          if (response.data.Error) {
            this.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.productList = response.data.Data;
          }
        }
      )
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  /**
   * Fetches the list of product registrations based on the provided search query and pagination details.
   * @param currentPage - Current Page Number (used for pagination)
   * @param pagerSize - Number of items per page (used for pagination)
   * @param searchQuery - Search query string to filter product registrations
   */

  GetSearchProductRegistrationFilterService = (
    currentPage: number,
    pagerSize: number,
    searchQuery: string,
    businessTypeId?: number
  ) => {
    this.inProgress = true;
  
    const params = new URLSearchParams({
      PageNo: currentPage.toString(),
      PageSize: pagerSize.toString(),
      Search: searchQuery || "",
    });
  
    if (businessTypeId !== undefined && businessTypeId !== null) {
      params.append("businessTypeId", businessTypeId.toString());
    }
  
    const url = `${URLConstants.GetSearchProductRegistrationFilter}?${params.toString()}`;
  
    return baseService
      .getRequest(url)
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<IProductManagementListVM>>
        ) => {
          if (response.data.Error) {
            this.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.productList = response.data.Data;
          }
        }
      )
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };
  

  /**
   * This function is used to map staffList to allStaffManagement List suitable for Grid Component.
   * @returns allStaffManagementsList
   */
  get allProductManagement(): IProductManagementList[] {
    if (this.productList?.ProductVM && this.productList.ProductVM?.length > 0)
      return this.productList.ProductVM.map((product) => {
        let language = getLanguageFromLocalStorage();
        let isDelete = true;
        let isEdit = true;
        let registrationArray: IOption[] = registrationTypeOptions.filter(
          (option: IOption) => option.id === product.RegistrationType
        );
        return {
          Id: product.Id,
          language: language,
          RegistrationType:
            registrationArray.length > 0
              ? registrationArray[0].id > 0 ? product.StoreName ?? emptyValue : product.HeadquarterName ?? emptyValue
              : emptyValue,
          BusinessTypeId: product.BusinessTypeId ? product.BusinessTypeId : -1,
          Category:
            product?.Category?.length > 0
              ? product?.Category?.map((obj: any) => obj.CategoryName)?.join(
                " > "
              )
              : "",
          ProductName: product.ProductName,
          PriceExcludingTax: product.PriceExcludingTax,
          PriceIncludingTax: product.PriceIncludingTax,
          TaxId: product.TaxId,
          TaxValue: product.TaxValue,
          TaxAmount: product.TaxAmount,
          BarCode: product.BarCode,
          ProductCategory: product.ProductCategory,
          Status: product.Status,
          StartDate: product.StartDate,
          EndDate: product.EndDate,
          Image: product.Image,
          Note: product.Note,
          Description: product.Description,
          UnitPrice: product.UnitPrice,
          isDelete: isDelete,
          isEdit: isEdit,
        };
      });
    return [];
  }

  /**
   * This function is used to Get StaffManagement Details by Id by calling an API.
   * @param id : The StaffManagement Identifier
   * @returns
   */

  GetProductManagementByIdService = (id: number, storeId?: number) => {
    this.productState.inProgress = true;
    let url = storeId ? `${URLConstants.GetProductById}/${id}?storeId=${storeId}` : `${URLConstants.GetProductById}/${id}`;
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IProductManagementVM>>) => {
          if (response.data.Error) {
            this.productState.error = response.data.Message;
          } else {
            let data = response.data.Data;
            this.product = data;
          }
        }
      )
      .catch((err: string) => {
        this.productState.error = err;
      })
      .finally(
        action(() => {
          this.productState.inProgress = false;
        })
      );
  };

  /**
   * This function provides initail values to the Add StaffManagement Form.
   * @returns StaffManagement Details
   *
   */
  get productDetail(): IAddProductManagement {
    if (this.product) {
      return {
        RegistrationType: this.product.RegistrationType ?? 0,
        BusinessTypeId: this.product.BusinessTypeId ?? -1,
        CategoryId: this.product.CategoryId ?? -1,
        CategoryPath: "",
        ProductName: this.product.ProductName ?? "",
        PriceExcludingTax: this.product.PriceExcludingTax ?? "",
        PriceIncludingTax: this.product.PriceIncludingTax ?? "",
        TaxId: this.product.TaxId ?? -1,
        TaxValue: this.product.TaxValue ?? 0,
        TaxAmount: this.product.TaxAmount ?? "",
        BarCode: this.product.BarCode ?? "",
        ProductCategory: this.product.ProductCategory
          ? this.product.ProductCategory.toString()
          : "0",
        Status: this.product.Status ? this.product.Status.toString() : "0",
        StartDate:
          this.product.StartDate == "0001-01-01T00:00:00.0000000Z"
            ? ""
            : (this.product.StartDate ? moment(this.product.StartDate).format(dateFormat) : ""),
        EndDate:
          this.product.EndDate == "0001-01-01T00:00:00.0000000Z"
            ? ""
            : (this.product.EndDate ? moment(this.product.EndDate).format(dateFormat) : ""),
        Image: this.product.Image ?? "",
        Note: this.product.Note ?? "",
        Description: this.product.Description ?? "",
        StoreId: this.product.StoreId,
        StoreName: this.product.StoreName ?? "",
        HeadquarterName: this.product.HeadquarterName ?? "",
        RegistrationTypeName: this.product.RegistrationType > 0 ? this.product.StoreName ?? "" : this.product.HeadquarterName ?? "",
        IsAutoCalculateTax: false,
        UnitPrice: this.product.UnitPrice ?? "",
        UnitPriceExcludingTax: this.product.UnitPriceExcludingTax ?? "",
        UnitPriceTaxAmount: this.product.UnitPriceTaxAmount ?? "",
        UnitPriceTaxId: this.product.UnitPriceTaxId ?? -1,
        UnitPriceTaxValue: this.product.UnitPriceTaxValue ?? 0,
      };
    }
    return {
      ...initialState, BusinessTypeId: getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.HeadQuarter] || getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.Store]
        ? getBusinessTypeIdFromLocalStorage() : -1,
      RegistrationType: getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.HeadQuarter] ? 0 : getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.Store] ? 1 : initialState.RegistrationType,
    }
  }
  /*
    This function is used to reset all staff observables to their initial values.  
    */
  resetProductManagementDetail = () => {
    this.product = undefined;
    this.productState = { ...this.initialStateValue };
  };

  /**
   * This function is used to Add New StaffManagement by calling an API.
   * @param data : StaffManagement Data
   * @returns
   */

  AddProductManagementService = (data: IAddProductManagement) => {
    this.addUpdateProductManagementState.inProgress = true;
    let url = URLConstants.AddProduct;
    const formData = new FormData();
    formData.append("RegistrationType", data.RegistrationType?.toString());
    formData.append("BusinessTypeId", data.BusinessTypeId?.toString());
    formData.append("CategoryId", data.CategoryId?.toString());
    formData.append("ProductName", data.ProductName);
    formData.append("PriceExcludingTax", data.PriceExcludingTax?.toString());
    formData.append("PriceIncludingTax", data.PriceIncludingTax?.toString());
    formData.append("TaxId", data.TaxId?.toString());
    formData.append("TaxValue", data.TaxValue?.toString());
    formData.append("TaxAmount", data.TaxAmount?.toString());
    formData.append("BarCode", data.BarCode);
    formData.append("ProductCategory", data.ProductCategory?.toString());
    formData.append("Status", data.Status.toString());
    formData.append("StartDate", data.StartDate);
    formData.append("EndDate", data.EndDate);
    formData.append("Image", data.Image);
    formData.append("Note", data.Note);
    formData.append("Description", data.Description);
    formData.append("UnitPrice", data.UnitPrice?.toString());
    formData.append("UnitPriceExcludingTax", data.UnitPriceExcludingTax?.toString());
    formData.append("UnitPriceTaxId", data.UnitPriceTaxId?.toString());
    formData.append("UnitPriceTaxValue", data.UnitPriceTaxValue?.toString());
    formData.append("UnitPriceTaxAmount", data.UnitPriceTaxAmount?.toString());
    if (getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.Store] && getStoreIdFromLocalStorage())
      formData.append("StoreId", getStoreIdFromLocalStorage().toString());
    return baseService
      .postMultipart(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateProductManagementState.error = response.data.Message;
        } else this.addUpdateProductManagementState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateProductManagementState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateProductManagementState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to Updating Existing StaffManagement Details by calling an API.
   * @param id : The StaffManagement Identifier
   * @param data
   * @returns
   */

  UpdateProductManagementService = (
    id: number,
    data: IAddProductManagement
  ) => {
    this.addUpdateProductManagementState.inProgress = true;
    let url = URLConstants.UpdateProduct;
    const formData = new FormData();
    formData.append("Id", id?.toString());
    formData.append("RegistrationType", data.RegistrationType?.toString());
    formData.append("BusinessTypeId", data.BusinessTypeId?.toString());
    formData.append("CategoryId", data.CategoryId?.toString());
    formData.append("ProductName", data.ProductName);
    formData.append("PriceExcludingTax", data.PriceExcludingTax?.toString());
    formData.append("PriceIncludingTax", data.PriceIncludingTax?.toString());
    formData.append("TaxId", data.TaxId?.toString());
    formData.append("TaxValue", data.TaxValue?.toString());
    formData.append("TaxAmount", data.TaxAmount?.toString());
    formData.append("BarCode", data.BarCode);
    formData.append("ProductCategory", data.ProductCategory?.toString());
    formData.append("Status", data.Status.toString());
    formData.append("StartDate", data.StartDate);
    formData.append("EndDate", data.EndDate);
    formData.append("Image", data.Image);
    formData.append("Note", data.Note);
    formData.append("Description", data.Description);
    formData.append("UnitPrice", data.UnitPrice.toString());
    formData.append("UnitPriceExcludingTax", data.UnitPriceExcludingTax?.toString());
    formData.append("UnitPriceTaxId", data.UnitPriceTaxId?.toString());
    formData.append("UnitPriceTaxValue", data.UnitPriceTaxValue?.toString());
    formData.append("UnitPriceTaxAmount", data.UnitPriceTaxAmount?.toString());
    formData.append("IsBarcodeChanged", data.IsBarcodeChanged ? "true" : "false");
    formData.append("IsCategoryChanged", data?.IsCategoryChanged ? "true" : "false");
    if(data.StoreId)
      formData.append("StoreId", data.StoreId?.toString());
    return baseService
      .putMultipart(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateProductManagementState.error = response.data.Message;
        } else this.addUpdateProductManagementState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateProductManagementState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateProductManagementState.inProgress = false;
        })
      );
  };

  /*
    This function is used to reset all AddStaffManagement observables to their initial values.  
    */
  resetAddUpdateProductManagementState = () => {
    this.addUpdateProductManagementState.inProgress = false;
    this.addUpdateProductManagementState.success = false;
    this.addUpdateProductManagementState.error = "";
  };

  /**
   * This function is used to Delete StaffManagement by calling an API.
   * @param id : The StaffManagement Identifier
   * @returns
   */

  DeleteProductManagementService = (id: number) => {
    this.deleteProductManagementState.inProgress = true;
    let url = `${URLConstants.DeleteProduct}/${id}`;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteProductManagementState.error = response.data.Message;
          // toast.error(formatMessage(response.data.Message));
        } else this.deleteProductManagementState.success = true;
      })
      .catch((err: string) => {
        this.deleteProductManagementState.error = err;
      })
      .finally(
        action(() => {
          this.deleteProductManagementState.inProgress = false;
        })
      );
  };

  /*
    This function is used to reset all AddStaffManagement observables to their initial values.  
    */
  resetDeleteProductManagement = () => {
    this.deleteProductManagementState.inProgress = false;
    this.deleteProductManagementState.success = false;
    this.deleteProductManagementState.error = "";
  };

  /**
   * This function is used to Updating Existing StaffManagement state (i.e. active/inactive) Details by calling an API.
   * @param id : The StaffManagement Identifier
   * @param state: StaffManagement state
   * @returns
   */
  UpdateProductManagementStatusService = (id: number, state: boolean) => {
    this.activeInactiveState.inProgress = true;
    let url = URLConstants.ProductManagementStatus;
    let productData: any = {
      Id: id,
      IsActive: state,
    };
    return baseService
      .putRequest(url, productData)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.activeInactiveState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.activeInactiveState.success = true;
      })
      .catch((err: string) => {
        this.activeInactiveState.error = err;
      })
      .finally(
        action(() => {
          this.activeInactiveState.inProgress = false;
        })
      );
  };

  /*
            This function is used to set the filter details to the Filter observable.  
            */
  setProductFilterDetail = (filter: IProductFilter) => {
    this.productFilter = { ...filter };
  };

  get getProductFilter(): IProductFilter {
    return this.productFilter;
  }

  GetProductListByCatIdService = (id: number, storeId: number, isDateRequired: boolean) => {
    this.getProductDataAccordionState.inProgress = true;
    const BusinessTypeId = getBusinessTypeIdFromLocalStorage();
    const urlParams = new URLSearchParams();
    urlParams.append('StoreId', String(storeId));
    urlParams.append('CategoryId', String(id));
    if (BusinessTypeId > 0) {
      urlParams.append('businessTypeId', String(BusinessTypeId));
    }

    let url = `${URLConstants.GetProductRegistartionStoreId}/CategoryId?${urlParams.toString()}&IsDateRequired=${isDateRequired}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error && !Array.isArray(response.data.Data)) {
          this.getProductDataAccordionState.error = response.data.Message;
        } else {
          let data = response.data.Data;
          if (Array.isArray(data)) {
            let mappedData = data.map((item: any) => {
              return {
                ...item.product,
                OrderId: item.OrderId,
                Id: item.ProductId,
                CategoryId: item.CategoryId,
                ProductDetailsId: item.Id,
              };
            })
            // mappedData.sort((a, b) => a.OrderId - b.OrderId);
            if (this.productAccordionData.has(id)) {
              // Replace existing data
              this.productAccordionData.set(id, mappedData);
            } else {
              // Append new entry
              this.productAccordionData.set(id, [
                ...(this.productAccordionData.get(id) || []),
                ...mappedData,
              ]);
            }
          }
        }
      })
      .catch((err: string) => {
        this.getProductDataAccordionState.error = err;
      })
      .finally(
        action(() => {
          this.getProductDataAccordionState.inProgress = false;
        })
      );
  };

  GetProductListBySearchService = (search: string) => {
    this.getProductDataAccordionState.inProgress = true;
    let storeId = getStoreIdFromLocalStorage();
    const BusinessTypeId = getBusinessTypeIdFromLocalStorage();
    const urlParams = new URLSearchParams();
    urlParams.append('StoreId', String(storeId));
    urlParams.append('Search', String(search));
    if (BusinessTypeId > 0) {
      urlParams.append('businessTypeId', String(BusinessTypeId));
    }
    let url = `${URLConstants.GetProductRegistartionStoreId}/Search?` + urlParams.toString();
    this.productAccordionData.clear();
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error && !Array.isArray(response.data.Data)) {
          this.getProductDataAccordionState.error = response.data.Message;
          this.productAccordionData.clear();
        } else {
          let data = response.data.Data;
          this.productAccordionData.clear();
          if (Array.isArray(data) && data.length > 0) {
            // data.sort((a, b) => a.OrderId - b.OrderId);
            data.forEach((item: any) => {
              let mappedData = {
                ...item.products,
                CategoryId: item.CategoryId,
                OrderId: item.OrderId,
                Id: item.products.Id,
                ProductDetailsId: item.Id,
              };
              if (this.productAccordionData.has(item.CategoryId)) {
                let prevData = this.productAccordionData.get(item.CategoryId) || [];
                this.productAccordionData.set(item.CategoryId, [...prevData, { ...mappedData }]);
              } else {
                this.productAccordionData.set(item.CategoryId, [
                  { ...mappedData },
                ]);
              }
            })
          }
        }
      })
      .catch((err: string) => {
        this.getProductDataAccordionState.error = err;
        this.productAccordionData.clear();
      })
      .finally(
        action(() => {
          this.getProductDataAccordionState.inProgress = false;
        })
      );
  };

  clearProductData = () => {
    this.productAccordionData.clear();
  };

  setProductStoreFilterDetail = (filter: IProductStoreFilter) => {
    this.productStoreFilter = { ...filter };
  };

  resetproductStoreFilterDetail = () => {
    this.productStoreFilter = { ...productInitialStoreFilterState };
  };

  get getProductStoreFilter(): IProductStoreFilter {
    return this.productStoreFilter;
  }

  get getProductData() {
    return this.productAccordionData
  };


  UpdateStoreProductOrderIdService = (data: any, storeId: number) => {
    this.addUpdateStoreProductOrderState.inProgress = true;
    let url = URLConstants.UpdateStoreProductOrderId;
    let mappedData = data.map((item: any) => {
      return {
        Id: item.ProductDetailsId,
        OrderId: item.OrderId,
        StoreId: storeId,
        CategoryId: item.CategoryId
      };
    })
    return baseService
      .putRequest(url, { productDetails: mappedData })
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateStoreProductOrderState.error = response.data.Message;
        } else { this.addUpdateStoreProductOrderState.success = true };
      })
      .catch((err: string) => {
        this.addUpdateStoreProductOrderState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateStoreProductOrderState.inProgress = false;
        })
      );
  };

  resetAddUpdateStoreProductOrderState = () => {
    this.addUpdateStoreProductOrderState = { ...this.initialStateValue }
  }
  /*
    This function is used to reset all observables to their initial values.  
    */
  reset = () => {
    this.error = "";
    this.inProgress = false;
    this.addUpdateProductManagementState.success = false;
    this.addUpdateProductManagementState.error = "";
    this.deleteProductManagementState = { ...this.initialStateValue };
    this.addUpdateProductManagementState.inProgress = false;
    this.productState = { ...this.initialStateValue };
  };

  /**
   * This function is used to reset all staff observables to their initial values.
   * @returns
   */
  resetProduct = () => {
    this.error = "";
    this.inProgress = false;
    this.productList = GetAllProductManagementState;

    this.addUpdateProductManagementState = { ...this.initialStateValue };
    this.deleteProductManagementState = { ...this.initialStateValue };
    this.product = undefined;
    this.productState = { ...this.initialStateValue };
  };
}

export default new ProductManagementStore();
